import { TMS_LAST_ACTIVITY_KEY } from '../constants';
import storage from './storage';

/** 闲置阈值：15 分钟 */
const IDLE_TIMEOUT = 15 * 60 * 1000;
/** 用户操作事件节流间隔，避免 mousemove 高频触发 */
const THROTTLE_INTERVAL = 1000;
/** 节流后闲置检查的最小重试间隔 */
const IDLE_RETRY_INTERVAL = 1000;

const ACTIVITY_EVENTS: string[] = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];

type IdleCallback = () => void;

/**
 * 统一活动追踪器（单例）
 *
 * 职责：
 * 1. 监听用户操作事件，维护 lastActivity 时间戳
 * 2. 多标签页同步：本标签页活跃时写入 localStorage，
 *    其他标签页通过 storage 事件同步活跃时间并重置闲置倒计时
 * 3. 15 分钟无操作时触发 onIdle 回调（弹出登录弹窗）
 *
 * 使用方式：
 * - 已登录后调用 activityTracker.start()，退出登录时调用 stop()
 * - 弹窗打开期间调用 pause() 暂停闲置判定，关闭后 resume() + reset()
 * - 心跳定时器（TODO 2）通过 getLastActivity() 读取活跃时间
 */
class ActivityTracker {
  /** 最后一次活跃时间（含其他标签页同步） */
  private lastActivity = Date.now();
  /** 上次同步到 localStorage 的时间，用于节流写入 */
  private lastSync = 0;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private idleCallbacks = new Set<IdleCallback>();
  private listening = false;
  /** 暂停闲置判定（弹窗打开期间），事件监听与跨页同步仍然生效 */
  private paused = false;
  /** 闲置回调已触发，需 reset() 后才能再次触发 */
  private idleFired = false;

  getLastActivity(): number {
    return this.lastActivity;
  }

  onIdle(callback: IdleCallback): () => void {
    this.idleCallbacks.add(callback);
    return () => {
      this.idleCallbacks.delete(callback);
    };
  }

  start(): void {
    if (this.listening) return;
    this.listening = true;
    const stored = this.readStoredActivity();
    // 启动时以 localStorage 中其他标签页的活跃时间为准，不做钳制：
    // start() 仅在已持有 token 时运行（登录成功路径会先 reset() 刷新存储），
    // 若残留的活跃时间已超阈值（如标签页被 Chrome 丢弃后重载），
    // 让 checkIdle 立即触发弹出重登弹窗，而不是静默重置计时
    this.lastActivity = stored ?? Date.now();
    if (stored === null) {
      storage.localSet(TMS_LAST_ACTIVITY_KEY, this.lastActivity);
    }
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, this.handleActivity, { passive: true });
    });
    window.addEventListener('storage', this.handleStorage);
    // 后台标签页定时器会被 Chrome 节流/冻结，切回时补判一次，
    // 且要先于用户的 mousemove（否则 touch() 会取消待触发的闲置检查）
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.scheduleIdleCheck();
  }

  stop(): void {
    if (!this.listening) return;
    this.listening = false;
    this.clearIdleTimer();
    ACTIVITY_EVENTS.forEach((event) => {
      window.removeEventListener(event, this.handleActivity);
    });
    window.removeEventListener('storage', this.handleStorage);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.idleFired = false;
    this.paused = false;
  }

  /** 暂停闲置判定（如登录弹窗打开期间） */
  pause(): void {
    this.paused = true;
  }

  /** 是否处于暂停（闲置重登弹窗打开期间）；401 处理据此跳过硬跳转 */
  isPaused(): boolean {
    return this.paused;
  }

  /** 恢复闲置判定并重新计时 */
  resume(): void {
    this.reset();
  }

  /** 重置闲置计时（登录成功、恢复活跃时调用），并同步到其他标签页 */
  reset(): void {
    this.idleFired = false;
    this.paused = false;
    this.touch(Date.now(), true);
  }

  private handleActivity = () => {
    const now = Date.now();
    if (now - this.lastSync < THROTTLE_INTERVAL) return;
    this.touch(now, true);
  };

  /**
   * 更新活跃时间
   * @param sync 是否写入 localStorage 同步给其他标签页
   */
  private touch(time: number, sync: boolean): void {
    this.lastActivity = time;
    if (sync) {
      this.lastSync = time;
      storage.localSet(TMS_LAST_ACTIVITY_KEY, time);
    }
    if (this.idleFired) return; // 闲置已触发，等 reset() 重新计时
    this.scheduleIdleCheck();
  }

  /** 其他标签页活跃时，同步活跃时间并重置本标签页的闲置倒计时 */
  private handleStorage = (e: StorageEvent) => {
    if (e.key !== TMS_LAST_ACTIVITY_KEY || e.newValue === null) return;
    const time = Number(e.newValue);
    if (!Number.isFinite(time) || time <= this.lastActivity) return;
    this.lastActivity = time;
    this.lastSync = 0; // 本标签页未产生操作，下次写入不再节流拦截
    this.idleFired = false;
    if (this.listening) {
      this.scheduleIdleCheck();
    }
  };

  /**
   * 标签页切回可见时立即补判闲置：
   * 后台期间定时器被 Chrome 节流/冻结可能未触发，若等用户的 mousemove
   * 会先重置活跃时间（touch 内 clearIdleTimer），导致永远不弹窗。
   * checkIdle 内的时间戳二次校验保证未真正闲置时不会误触发。
   */
  private handleVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    if (!this.listening || this.paused || this.idleFired) return;
    this.checkIdle();
  };

  private scheduleIdleCheck(): void {
    this.clearIdleTimer();
    const remaining = IDLE_TIMEOUT - (Date.now() - this.lastActivity);
    this.idleTimer = setTimeout(this.checkIdle, Math.max(remaining, 0));
  }

  private checkIdle = () => {
    // 用时间戳二次校验，规避后台标签页定时器节流导致的提前触发
    const idleFor = Date.now() - this.lastActivity;
    if (idleFor < IDLE_TIMEOUT) {
      this.scheduleIdleCheck();
      return;
    }
    if (this.paused) {
      // 暂停期间不触发，低频轮询等待恢复
      this.idleTimer = setTimeout(this.checkIdle, IDLE_RETRY_INTERVAL);
      return;
    }
    this.idleFired = true;
    this.idleCallbacks.forEach((callback) => callback());
  };

  private readStoredActivity(): number | null {
    try {
      const time = storage.localGet(TMS_LAST_ACTIVITY_KEY);
      return typeof time === 'number' && Number.isFinite(time) ? time : null;
    } catch {
      return null;
    }
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}

const activityTracker = new ActivityTracker();
export default activityTracker;
